import React from 'react';
import { inject, observer } from 'mobx-react';
import { IReactionDisposer, makeObservable, observable, reaction } from 'mobx';
import autobind from 'autobind';

import { WithTranslation, withTranslation } from 'react-i18next';

import { Ehr } from '@patient/store/ehr/Ehr';
import { ViewPortState } from '@store/ViewPort';
import { AppState } from '@store/App/App';
import { AuthUser } from '@store/AuthUser/AuthUser';

import { OrganizationList } from '@organization/store/organization/OrganizationList';
// import { appService } from '@store/App/appService';
import { authUserService } from '@store/AuthUser/services/authUserService';

import { Page, PageProps } from '@core/Page/Page';
import { TaskRightMenuAction } from '@common/Task';
// import { featureToggleService } from '@store/Configuration/featureToggleService';

import { withRouter, WithRouterProps } from '@hoc/withRouterProps';

interface Props extends PageProps {}

interface StoreProps {
    authUser: AuthUser;
    viewPort: ViewPortState;
    app: AppState;
    ehr: Ehr;
    organizationList: OrganizationList;
}

const injectableStores: (keyof StoreProps)[] = [
    ViewPortState.Name,
    AppState.Name,
    AuthUser.Name,
    Ehr.Name,
    OrganizationList.Name,
];

@(withRouter as any)
@observer
class Component extends React.Component<Props & StoreProps & WithTranslation & WithRouterProps> {
    @observable private isPhoneWidgetHide = false;
    private disposeReaction?: IReactionDisposer;

    private content: React.RefObject<HTMLDivElement | null> = React.createRef();

    constructor(props: Props & StoreProps & WithTranslation & WithRouterProps) {
        super(props);

        makeObservable(this);
    }

    public async componentDidMount(): Promise<void> {
        const { app, viewPort, authUser } = this.props;

        app.leftMenu.isOpen = false;
        app.headerView.setActions([TaskRightMenuAction]);

        viewPort.updatePageHtmlElement();

        this.disposeReaction = reaction(
            () => [authUser.isAuthorized, authUser.isTimeLeft],
            () => {
                if (authUser.isAuthorized && authUser.isTimeLeft) {
                    this.handleAuthorizeClick();
                }
            },
        );
    }

    public componentDidUpdate() {
        const { app, viewPort, location } = this.props;

        if (location.pathname === '/schedule') {
            app.withoutCustomScroll = viewPort.isMobileSize;
        } else {
            app.withoutCustomScroll = location.pathname.includes('/report');
        }
    }

    public componentWillUnmount(): void {
        if (!!this.disposeReaction) {
            this.disposeReaction();
        }
    }

    public render(): React.ReactNode {
        const { viewPort, app, ehr, authUser, ...props } = this.props;
        const {
            rootFixed,
            forbiddenResources,
            withoutHeader,
            hideScrollBar,
            greyBackground,
            withoutLeftMenu,
            withContentNavigation,
            withoutCustomScroll,
            isOnLoad,
            isOnUpdate,
            showLoader,
            showingPopupParams,
            confirmPopupParams,
            existingReferralPopup,
            selectOrganizationPopupParams,
            scrollBarRef,
            appOverlay,
        } = app;

        const { isAuthorized } = authUser;

        return React.createElement(Page, {
            ...props,
            appOverlay,
            rootFixed,
            forbiddenResources,
            withoutHeader,
            hideScrollBar,
            greyBackground,
            withoutLeftMenu,
            withoutCustomScroll,
            withContentNavigation,
            isOnLoad,
            isOnUpdate,
            showLoader,
            showingPopupParams,
            confirmPopupParams,
            selectOrganizationPopupParams,
            existingReferralPopup,
            isAuthorized,
            scrollBarRef,
            isPhoneWidgetHide: this.isPhoneWidgetHide,
            showBlockBanner: ehr.showBlockBanner,
            blockBannerUrl: ehr.blockBannerUrl,
            phoneWidgetOpen: app.phoneWidget.isOpen,
            additionalLeftMenuContent: app.headerView.leftContent?.component,
            needAuthorize: authUser.isAuthorized && authUser.isTimeLeft,
            contentRef: this.content,
            withPaddingTop: app.withPaddingTop,
            withPaddingBottom: app.withPaddingBottom,
            onClosePopupClick: this.onClosePopupClick,
            onScroll: this.onScroll,
            onAuthorizeClick: this.handleAuthorizeClick,
            onClickOutsidePhoneMask: this.handleClickOutsidePhoneWidget,
        });
    }

    @autobind
    private async handleAuthorizeClick(): Promise<void> {
        await authUserService.logout();
    }

    @autobind
    private handleClickOutsidePhoneWidget(): void {
        const { phoneWidget } = this.props.app;
        if (!phoneWidget.hasActiveSession) {
            phoneWidget.initialPhoneNumber = '';
            phoneWidget.isOpen = false;
        }
    }

    @autobind
    private onClosePopupClick(): void {
        this.props.app.showingPopupParams = null;
        this.props.app.confirmPopupParams = null;
    }

    @autobind
    private onScroll(event: React.UIEvent<HTMLDivElement>): void {
        const { scrollLeft, scrollTop, scrollHeight, clientHeight } = <HTMLInputElement>event.target;
        const { app, viewPort } = this.props;

        /*
        Fix scroll on IPhone
        http://qaru.site/questions/1217128/website-often-refuses-to-scroll-on-iphone-safari/3936529#3936529
        */
        const calculatedScrollTop = Math.max(1, Math.min(scrollTop, scrollHeight - clientHeight - 1));

        viewPort.setScrollPosition(calculatedScrollTop, scrollLeft);
        app.pageScrollHandler?.(event);
    }
}

export const PagePageContainer = withTranslation()(inject<Props, StoreProps>(...injectableStores)(Component));
